import React, { useEffect, useState } from 'react';

/* The landing page's instrument clock, portable: an SVG dial telling real
   local time. Hands wind from 12:00 to now on mount, then tick once per
   second. Static under prefers-reduced-motion. Size it via the parent. */

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function useClockAngles() {
  const compute = () => {
    const now = new Date();
    const s = now.getSeconds();
    const m = now.getMinutes();
    const h = now.getHours() % 12;
    return { sec: s * 6, min: m * 6 + s * 0.1, hour: h * 30 + m * 0.5 };
  };
  const [angles, setAngles] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setAngles(compute()), 1000);
    return () => clearInterval(id);
  }, []);
  return angles;
}

const DAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export default function InstrumentClock() {
  const reduced = usePrefersReducedMotion();
  const angles = useClockAngles();
  const [phase, setPhase] = useState(reduced ? 'run' : 'start');
  useEffect(() => {
    if (reduced) { setPhase('run'); return undefined; }
    let t2;
    const t1 = setTimeout(() => {
      setPhase('wind');
      t2 = setTimeout(() => setPhase('run'), 1750);
    }, 80);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduced]);

  const shown = phase === 'start' ? { sec: 0, min: 0, hour: 0 } : angles;
  const hand = (deg) => ({ transform: `rotate(${deg}deg)`, transformOrigin: '300px 300px' });

  const now = new Date();
  const dayDate = `${DAY_ABBR[now.getDay()]} ${String(now.getDate()).padStart(2, '0')}`;

  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const major = i % 5 === 0;
    const a = (i * 6 * Math.PI) / 180;
    const r1 = major ? 268 : 278;
    ticks.push(
      <line
        key={i}
        x1={300 + Math.sin(a) * r1} y1={300 - Math.cos(a) * r1}
        x2={300 + Math.sin(a) * 288} y2={300 - Math.cos(a) * 288}
        className={major ? 'ic-tick-major' : 'ic-tick'}
      />
    );
  }

  return (
    <svg className={`ic-clock ${phase === 'wind' ? 'winding' : ''}`} viewBox="0 0 600 600" aria-hidden="true">
      <circle cx="300" cy="300" r="296" className="ic-rim" />
      <circle cx="300" cy="300" r="180" className="ic-inner" />
      {ticks}
      <text x="300" y="64" className="ic-numeral">12</text>
      <text x="536" y="300" className="ic-numeral">3</text>
      <text x="300" y="536" className="ic-numeral">6</text>
      <text x="64" y="300" className="ic-numeral">9</text>
      <text x="300" y="404" className="ic-date">{dayDate}</text>
      <g className="ic-hand-hour" style={hand(shown.hour)}>
        <line x1="300" y1="300" x2="300" y2="170" />
      </g>
      <g className="ic-hand-min" style={hand(shown.min)}>
        <line x1="300" y1="300" x2="300" y2="100" />
      </g>
      <g className="ic-hand-sec" style={hand(shown.sec)}>
        <line x1="300" y1="330" x2="300" y2="88" />
        <circle cx="300" cy="300" r="5" />
      </g>
      <circle cx="300" cy="300" r="3" className="ic-pinion" />
    </svg>
  );
}
